/**
 * Payment Links Actions
 * Chamadas à API real para gerenciamento de links de pagamento
 */

import axios from 'axios';
import { BASE_API, USER_TOKEN } from 'config/consts';
import * as XLSX from 'xlsx';
import types from './types';

const URL = `${BASE_API}/payment-links`;

const SYNCABLE_PAYMENT_LINK_STATUSES = ['pending', 'waiting_payment'];

/**
 * Vocabulário único dos status de link e de cobrança.
 *
 * PaymentLinks.status e PaymentLinkCharges.status têm o MESMO ENUM de 14
 * valores (o STATUS_DO_ENUM de main-api/src/services/gateway/status.js), mas
 * cada tela guardava o seu recorte: a lista caía em `statusConfig.pending` e
 * chamava de "Pendente" um link recusado, enquanto o Relatório de Vendas e o
 * Excel desta mesma página já diziam "Recusado". Uma fonte só impede que a
 * mesma linha tenha dois nomes em telas diferentes.
 */
const CHIP_PENDENTE = { backgroundColor: '#fff3cd', color: '#856404' };
const CHIP_PAGO = { backgroundColor: '#d4edda', color: '#155724' };
const CHIP_NEGATIVO = { backgroundColor: '#f8d7da', color: '#721c24' };
const CHIP_ENCERRADO = { backgroundColor: '#f8f9fa', color: '#6c757d' };
const CHIP_ESTORNADO = { backgroundColor: '#e2e3e5', color: '#383d41' };

export const STATUS_DO_LINK = {
  pending: { label: 'Pendente', style: CHIP_PENDENTE },
  processing: { label: 'Processando', style: CHIP_PENDENTE },
  waiting_payment: { label: 'Aguardando', style: CHIP_PENDENTE },
  paid: { label: 'Pago', style: CHIP_PAGO },
  overpaid: { label: 'Pago a maior', style: CHIP_PAGO },
  // Pago a menor ainda não fechou a conta: o backend conta 'underpaid' entre os
  // pendentes (STATUS_PENDENTES), não entre os pagos.
  underpaid: { label: 'Pago a menor', style: CHIP_PENDENTE },
  refused: { label: 'Recusado', style: CHIP_NEGATIVO },
  // 'failed' é como a Core API v5 nomeia a mesma recusa de 'refused'.
  failed: { label: 'Recusado', style: CHIP_NEGATIVO },
  unpaid: { label: 'Inadimplente', style: CHIP_NEGATIVO },
  expired: { label: 'Expirado', style: CHIP_ENCERRADO },
  canceled: { label: 'Cancelado', style: CHIP_NEGATIVO },
  refunded: { label: 'Estornado', style: CHIP_ESTORNADO },
  // O banco grava 'partial_canceled'; 'partially_refunded' nunca existiu no ENUM.
  partial_canceled: { label: 'Parcialmente Estornado', style: CHIP_PENDENTE },
  chargedback: { label: 'Chargeback', style: CHIP_NEGATIVO },
};

/**
 * Chip de um status de link.
 *
 * Termo fora do ENUM não vira "Pendente": o fallback antigo pintava de amarelo
 * e afirmava um desfecho que não existia, escondendo justamente a recusa. Agora
 * ele aparece cru e em cinza, sem afirmar nada.
 */
export const statusDoLink = (status) =>
  STATUS_DO_LINK[status] || { label: status || '—', style: CHIP_ENCERRADO };

/** Os mesmos status no feminino das cobranças ("Paga", "Recusada"). */
export const STATUS_DA_COBRANCA = {
  pending: 'Aguardando',
  waiting_payment: 'Aguardando',
  processing: 'Processando',
  paid: 'Paga',
  overpaid: 'Paga a maior',
  underpaid: 'Paga a menor',
  expired: 'Expirada',
  canceled: 'Cancelada',
  refunded: 'Estornada',
  partial_canceled: 'Parcialmente estornada',
  chargedback: 'Chargeback',
  refused: 'Recusada',
  failed: 'Recusada',
  unpaid: 'Não paga',
};

/** Cobre o ENUM inteiro; o termo cru sobra só para valor que o banco não grava. */
export const rotuloDaCobranca = (status) => STATUS_DA_COBRANCA[status] || status || '—';

/**
 * Por que o painel não pode estornar uma cobrança.
 *
 * As chaves são as de `motivo_bloqueio` em GET /:linkId/refundable-charges
 * (gestaoDeLink.motivoDeBloqueioDoEstorno). A tela escrevia "Expirado" nos três
 * casos, então quem via uma cobrança anterior à migração não descobria que o
 * caminho é o Dashboard da Pagar.me — a mensagem existia no backend e nunca
 * chegava ao gestor.
 */
const MOTIVOS_DE_BLOQUEIO = {
  cobranca_anterior_a_migracao: {
    curto: 'Anterior à migração',
    detalhe: () => 'Cobrança anterior à migração: faça o estorno pelo Dashboard da Pagar.me.',
  },
  boleto: {
    curto: 'Boleto',
    detalhe: () => 'O estorno de boleto deve ser feito pelo Dashboard da Pagar.me.',
  },
  prazo: {
    curto: 'Fora do prazo',
    detalhe: ({ dias, prazoDias }) =>
      dias === null
        ? `Não foi possível calcular há quantos dias esta cobrança foi paga. O estorno automático vale por ${prazoDias} dias após o pagamento; passado o prazo, acione o suporte da Pagar.me.`
        : `Paga há ${dias} dias: o estorno automático vale por ${prazoDias} dias. Passado o prazo, acione o suporte da Pagar.me.`,
  },
};

/** Prazo padrão, usado só até a rota responder com o seu `prazo_dias`. */
export const PRAZO_DE_ESTORNO_PADRAO = 90;

/**
 * Dias desde o pagamento, ou null quando a rota não soube calcular.
 *
 * `dias_desde_pagamento` vem de um differenceInCalendarDays que depende de
 * `paid_at`: sem data ele chega nulo. `Number(null)` é 0, e 0 passa no
 * Number.isFinite — a tela afirmava "Paga há 0 dias", ou seja, paga hoje,
 * justamente quando não havia data nenhuma. Só número de verdade vira dias.
 */
const diasDesdeOPagamento = (valor) => {
  if (valor === null || valor === undefined || valor === '') return null;
  const dias = Number(valor);
  return Number.isFinite(dias) ? dias : null;
};

/**
 * Motivo do bloqueio pronto para a tela; null quando a cobrança é estornável.
 */
export const bloqueioDoEstorno = (cobranca, prazoDias = PRAZO_DE_ESTORNO_PADRAO) => {
  if (!cobranca || cobranca.estornavel) return null;
  const motivo = MOTIVOS_DE_BLOQUEIO[cobranca.motivo_bloqueio];
  if (motivo) {
    const dias = diasDesdeOPagamento(cobranca.dias_desde_pagamento);
    return { curto: motivo.curto, detalhe: motivo.detalhe({ dias, prazoDias }) };
  }
  // Motivo novo no backend não pode virar "Expirado" nem sumir da tela.
  return {
    curto: 'Indisponível',
    detalhe:
      'Esta cobrança não pode ser estornada pelo painel. Confira a cobrança no Dashboard da Pagar.me.',
  };
};

/**
 * Quanto ainda resta do prazo, para a coluna "Prazo" da cobrança estornável.
 *
 * Mesmo campo, mesma armadilha do bloqueio: sem `dias_desde_pagamento` a conta
 * `prazoDias - dias` dava NaN ("NaN dias") ou o prazo cheio como se a cobrança
 * fosse de hoje. Sem a informação, a coluna mostra um travessão.
 */
export const prazoRestanteDoEstorno = (cobranca, prazoDias = PRAZO_DE_ESTORNO_PADRAO) => {
  const dias = diasDesdeOPagamento(cobranca && cobranca.dias_desde_pagamento);
  if (dias === null) return '—';
  return `${Math.max(0, prazoDias - dias)} dias`;
};

/** Explicações distintas das cobranças bloqueadas, na ordem em que aparecem. */
export const motivosDeBloqueioDoEstorno = (cobrancas = [], prazoDias = PRAZO_DE_ESTORNO_PADRAO) => {
  const vistos = new Set();
  return cobrancas.reduce((lista, cobranca) => {
    const bloqueio = bloqueioDoEstorno(cobranca, prazoDias);
    if (bloqueio && !vistos.has(bloqueio.detalhe)) {
      vistos.add(bloqueio.detalhe);
      lista.push(bloqueio.detalhe);
    }
    return lista;
  }, []);
};

/**
 * Por que "Confirmar estorno" está desabilitado; null quando há o que estornar.
 *
 * Sem cobrança estornável nada fica selecionável e o botão nunca habilita — o
 * gestor precisa ler aqui o motivo, não deduzir do botão apagado.
 */
export const motivoDeEstornoIndisponivel = (
  cobrancas = [],
  prazoDias = PRAZO_DE_ESTORNO_PADRAO
) => {
  if (!cobrancas.length) return 'Este link não possui cobrança paga que possa ser estornada.';
  if (cobrancas.some((cobranca) => cobranca.estornavel)) return null;
  return [
    'Nenhuma cobrança deste link pode ser estornada pelo painel, então "Confirmar estorno" fica desabilitado.',
    ...motivosDeBloqueioDoEstorno(cobrancas, prazoDias),
  ].join(' ');
};

/**
 * Status de link que o backend considera pago — o STATUS_PAGOS de
 * main-api/src/services/gateway/status.js. 'overpaid' entra: pagamento a maior
 * é pagamento, e é dele que a lista já diz "Pago a maior".
 */
export const STATUS_PAGOS_DO_LINK = ['paid', 'overpaid'];

/**
 * O backend aceita abrir o estorno deste link?
 *
 * PUT /:linkId/refund não olha o status do link: ele pergunta a
 * listarCobrancasEstornaveis, que procura a cobrança em duas fontes, nesta
 * ordem — o histórico de ciclos (PaymentLinkCharges em STATUS_PAGOS, com
 * transação) e, na falta dele, a cobrança do próprio link (status em
 * STATUS_PAGOS). Espelhar as duas aqui é o que separa "o backend recusa" de "a
 * tela não deixa tentar": o botão exigia status exatamente 'paid' e trancava o
 * gestor fora de um link 'overpaid' e do segundo estorno parcial de um
 * recorrente, que o backend aceitaria.
 *
 * Quando é o link que sustenta o estorno, a decisão fica no status e o diálogo
 * explica o resto (cobrança legada, boleto, fora do prazo, sem transação) — a
 * rota devolve essas mensagens e elas só chegam ao gestor se a tela abrir.
 */
export const podeEstornarLink = (link) => {
  if (!link) return false;
  const cobrancas = Array.isArray(link.charges) ? link.charges : [];
  const temCicloPago = cobrancas.some(
    (cobranca) => STATUS_PAGOS_DO_LINK.includes(cobranca.status) && Boolean(cobranca.transactionId)
  );
  return temCicloPago || STATUS_PAGOS_DO_LINK.includes(link.status);
};

export const getPaymentLinks = (stationId, filters = {}, options = {}) => {
  return async (dispatch) => {
    if (!options.silent) {
      dispatch({ type: types.PAYMENT_LINKS_LOADING, payload: true });
    }

    try {
      const token = JSON.parse(localStorage.getItem(USER_TOKEN));
      // Construir query params
      const params = {};
      if (filters.status && filters.status !== 'all') {
        params.status = filters.status;
      }
      if (filters.startDate) {
        params.startDate = filters.startDate;
      }
      if (filters.endDate) {
        params.endDate = filters.endDate;
      }
      if (filters.search) {
        params.search = filters.search;
      }

      const response = await axios.get(`${URL}/station/${stationId}`, {
        params,
        headers: {
          Authorization: `${token}`,
        },
      });

      const data = response.data;

      dispatch({
        type: types.PAYMENT_LINKS_FETCHED,
        payload: {
          list: data.list || [],
          metrics: data.metrics || {},
          total: data.total || 0,
        },
      });

      return data;
    } catch (error) {
      console.error('Error fetching payment links:', error);
      if (!options.silent) {
        dispatch({
          type: 'SNACKBAR',
          payload: {
            message: 'Erro ao carregar links de pagamento',
            variant: 'error',
            open: true,
          },
        });
      }
      throw error;
    } finally {
      if (!options.silent) {
        dispatch({ type: types.PAYMENT_LINKS_LOADING, payload: false });
      }
    }
  };
};

export const getPaymentLinkCharges = (linkId) => {
  return async (dispatch) => {
    try {
      const token = JSON.parse(localStorage.getItem(USER_TOKEN));
      const response = await axios.get(`${URL}/${linkId}/charges`, {
        headers: {
          Authorization: `${token}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching payment link charges:', error);
      dispatch({
        type: 'SNACKBAR',
        payload: {
          message: 'Erro ao carregar histórico de cobranças',
          variant: 'error',
          open: true,
        },
      });
      throw error;
    }
  };
};

export const syncPaymentLinksStatuses = (links = []) => {
  return async (dispatch) => {
    const linksToSync = links.filter(
      (link) => link?.id && SYNCABLE_PAYMENT_LINK_STATUSES.includes(link.status)
    );

    if (!linksToSync.length) {
      dispatch({
        type: 'SNACKBAR',
        payload: {
          message: 'Nenhum pagamento pendente para sincronizar nesta página',
          variant: 'success',
          open: true,
        },
      });

      return {
        total: 0,
        checked: 0,
        paid: 0,
        failed: 0,
      };
    }

    const results = await Promise.allSettled(
      linksToSync.map((link) => axios.get(`${URL}/check-status/${link.id}`))
    );

    const checked = results.filter((result) => result.status === 'fulfilled').length;
    const paid = results.filter(
      (result) =>
        result.status === 'fulfilled' &&
        (result.value.data?.paid || result.value.data?.status === 'paid')
    ).length;
    const failed = results.length - checked;

    dispatch({
      type: 'SNACKBAR',
      payload: {
        message:
          failed > 0
            ? `Sincronização concluída com ${paid} pagamento(s) confirmado(s) e ${failed} falha(s)`
            : `Sincronização concluída com ${paid} pagamento(s) confirmado(s)`,
        variant: failed > 0 ? 'warning' : 'success',
        open: true,
      },
    });

    return {
      total: linksToSync.length,
      checked,
      paid,
      failed,
    };
  };
};

export const createPaymentLink = (data) => {
  return async (dispatch) => {
    dispatch({ type: types.PAYMENT_LINK_CREATING, payload: true });

    try {
      // Simular delay de rede
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Criar link mockado
      // const newLink = {
      //   id: `LNK${String(Math.floor(Math.random() * 10000)).padStart(3, '0')}`,
      //   ...data,
      //   status: 'pending',
      //   createdAt: new Date().toISOString(),
      //   url: `https://pagamento.exemplo.com/link/${Math.random().toString(36).substring(7)}`,
      //   fee: data.value * 0.03, // 3% de taxa
      //   stationValue: data.value * (data.stationPercentage / 100),
      //   partnerValue: data.hasPartner ? data.value * (data.partnerPercentage / 100) : 0,
      // };

      dispatch({
        type: types.PAYMENT_LINK_CREATED,
        payload: newLink,
      });

      dispatch({
        type: 'SNACKBAR',
        payload: {
          message: 'Link de pagamento criado com sucesso!',
          variant: 'success',
          open: true,
        },
      });

      return newLink;
    } catch (error) {
      console.error('Error creating payment link:', error);
      dispatch({
        type: 'SNACKBAR',
        payload: {
          message: 'Erro ao criar link de pagamento',
          variant: 'error',
          open: true,
        },
      });
      throw error;
    } finally {
      dispatch({ type: types.PAYMENT_LINK_CREATING, payload: false });
    }
  };
};

export const updatePaymentLinkStatus = (linkId, status) => {
  return async (dispatch) => {
    try {
      const token = JSON.parse(localStorage.getItem(USER_TOKEN));

      const response = await axios.put(
        `${URL}/${linkId}/status`,
        { status },
        {
          headers: {
            Authorization: `${token}`,
          },
        }
      );

      if (response.data.success) {
        dispatch({
          type: types.PAYMENT_LINK_UPDATED,
          payload: { id: linkId, status },
        });

        dispatch({
          type: 'SNACKBAR',
          payload: {
            message: 'Status atualizado com sucesso!',
            variant: 'success',
            open: true,
          },
        });

        return response.data;
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('Error updating payment link status:', error);
      dispatch({
        type: 'SNACKBAR',
        payload: {
          message: error.response?.data?.message || 'Erro ao atualizar status',
          variant: 'error',
          open: true,
        },
      });
      throw error;
    }
  };
};

export const cancelPaymentLink = (linkId) => {
  return async (dispatch) => {
    try {
      const token = JSON.parse(localStorage.getItem(USER_TOKEN));

      const response = await axios.put(
        `${URL}/${linkId}/cancel`,
        {},
        {
          headers: {
            Authorization: `${token}`,
          },
        }
      );

      if (response.data.success) {
        dispatch({
          type: types.PAYMENT_LINK_UPDATED,
          payload: { id: linkId, status: 'canceled' },
        });

        dispatch({
          type: 'SNACKBAR',
          payload: {
            message: 'Link cancelado com sucesso!',
            variant: 'success',
            open: true,
          },
        });

        return response.data;
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('Error canceling payment link:', error);
      dispatch({
        type: 'SNACKBAR',
        payload: {
          message: error.response?.data?.message || 'Erro ao cancelar link',
          variant: 'error',
          open: true,
        },
      });
      throw error;
    }
  };
};

/**
 * Cobranças de um link que ainda podem ser estornadas.
 * Em link recorrente há uma por ciclo, cada uma com seu próprio prazo.
 */
export const fetchRefundableCharges = (linkId) => {
  return async (dispatch) => {
    try {
      const token = JSON.parse(localStorage.getItem(USER_TOKEN));
      const response = await axios.get(`${URL}/${linkId}/refundable-charges`, {
        headers: { Authorization: `${token}` },
      });
      return response.data;
    } catch (error) {
      dispatch({
        type: 'SNACKBAR',
        payload: {
          message: error.response?.data?.message || 'Erro ao carregar cobranças do link',
          variant: 'error',
          open: true,
        },
      });
      return { charges: [] };
    }
  };
};

export const REFUND_PROCESSING_MESSAGE =
  'Estorno em processamento: a Pagar.me ainda vai confirmar a devolução. O status do link muda quando a confirmação chegar.';

/** O backend aceitou o estorno, mas a Pagar.me ainda não o confirmou. */
export const isRefundProcessing = (data) => data?.refund?.status === 'processing';

export const refundPaymentLink = (linkId, isPartial = false, value = null, chargeId = null) => {
  return async (dispatch) => {
    try {
      const token = JSON.parse(localStorage.getItem(USER_TOKEN));

      const response = await axios.put(
        `${URL}/${linkId}/refund`,
        { is_partial: isPartial, value, charge_id: chargeId },
        {
          headers: {
            Authorization: `${token}`,
          },
        }
      );

      if (response.data.success) {
        // 'processing': a Pagar.me aceitou o pedido, mas a devolução ainda não
        // foi confirmada e o backend não mudou o status do link. Marcar como
        // estornado aqui mostraria na tela algo que o banco não gravou.
        const emProcessamento = isRefundProcessing(response.data);

        if (!emProcessamento) {
          dispatch({
            type: types.PAYMENT_LINK_UPDATED,
            payload: {
              id: linkId,
              // Estorno parcial não deixa o link como 'refunded': o termo é o
              // mesmo que o banco grava, para tela e relatório não divergirem.
              status: isPartial ? 'partial_canceled' : 'refunded',
            },
          });
        }

        dispatch({
          type: 'SNACKBAR',
          payload: {
            message: emProcessamento
              ? REFUND_PROCESSING_MESSAGE
              : response.data.message || 'Estorno realizado com sucesso!',
            variant: emProcessamento ? 'info' : 'success',
            open: true,
          },
        });

        return response.data;
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('Error refunding payment link:', error);
      dispatch({
        type: 'SNACKBAR',
        payload: {
          message: error.response?.data?.message || 'Erro ao realizar estorno',
          variant: 'error',
          open: true,
        },
      });
      throw error;
    }
  };
};

export const attachInvoice = (linkId, file) => {
  return async (dispatch) => {
    try {
      // Simular delay de upload
      await new Promise((resolve) => setTimeout(resolve, 1500));

      const formData = new FormData();
      formData.append('invoice', file);

      const response = await axios.post(`${URL}/${linkId}/invoice`, formData, {
        headers: {
          Authorization: `${JSON.parse(localStorage.getItem(USER_TOKEN))}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      dispatch({
        type: 'SNACKBAR',
        payload: {
          message: 'Nota fiscal anexada com sucesso!',
          variant: 'success',
          open: true,
        },
      });

      return { success: true, fileName: file.name };
    } catch (error) {
      console.error('Error attaching invoice:', error);
      dispatch({
        type: 'SNACKBAR',
        payload: {
          message: 'Erro ao anexar nota fiscal',
          variant: 'error',
          open: true,
        },
      });
      throw error;
    }
  };
};

export const exportPaymentLinks = (data) => {
  return (dispatch) => {
    try {
      // O Excel sai do mesmo STATUS_DO_LINK do chip da lista. O recorte que
      // ficava aqui tinha vocabulário próprio — chamava de "Aberto" o que a
      // tela chama de "Pendente" e de "Faturado" o que a tela chama de "Pago" —
      // e juntava pending, waiting_payment e processing num rótulo só: quem
      // conferia a planilha contra a tela via dois nomes para a mesma linha.
      // Sem status a célula fica vazia: o travessão do chip é enfeite de tela e
      // atrapalharia o filtro da planilha.
      const statusLabel = (status) => (status ? statusDoLink(status).label : '');

      const formatDateTime = (iso) => {
        if (!iso) return '';
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return '';
        return `${d.toLocaleDateString('pt-BR')} ${d.toLocaleTimeString('pt-BR', {
          hour: '2-digit',
          minute: '2-digit',
        })}`;
      };

      // Preparar dados para exportação
      const exportData = data.map((link) => {
        const isRecurring = link.recurrence_type && link.recurrence_type !== 'single';
        const lastUpdateSource = link.lastPaymentUpdate || link.paidAt || link.updatedAt;

        return {
          ID: link.id,
          Status: statusLabel(link.status),
          Nome: link.name,
          CPF: link.cpf,
          'E-mail': link.email || '',
          Telefone: link.phone || '',
          'Atividade/Serviço': link.activity || 'Link Individual',
          'Data de Criação': new Date(link.createdAt).toLocaleDateString('pt-BR'),
          'Data de Vencimento': link.expiresAt
            ? new Date(link.expiresAt).toLocaleDateString('pt-BR')
            : '',
          'Valor Bruto': `R$ ${parseFloat(link.value).toFixed(2).replace('.', ',')}`,
          Taxa: `R$ ${parseFloat(link.fee || 0)
            .toFixed(2)
            .replace('.', ',')}`,
          'Valor Líquido': `R$ ${parseFloat(link.value - (link.fee || 0))
            .toFixed(2)
            .replace('.', ',')}`,
          'Valor Parceiro': link.partnerValue
            ? `R$ ${parseFloat(link.partnerValue).toFixed(2).replace('.', ',')}`
            : '',
          'Valor Estação': `R$ ${parseFloat(link.stationValue || 0)
            .toFixed(2)
            .replace('.', ',')}`,
          'Forma de Pagamento': link.paymentMethod || '',
          Parcelamento: link.installments ? `${link.installments}x` : '',
          'Nota Fiscal': link.hasInvoice ? 'Sim' : 'Não',
          'É recorrente?': isRecurring ? 'Sim' : 'Não',
          'Última atualização do pagamento':
            isRecurring && lastUpdateSource ? formatDateTime(lastUpdateSource) : '',
        };
      });

      // Criar workbook e worksheet
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Links de Pagamento');

      // Ajustar largura das colunas
      const colWidths = [
        { wch: 10 }, // ID
        { wch: 12 }, // Status
        { wch: 30 }, // Nome
        { wch: 15 }, // CPF
        { wch: 30 }, // E-mail
        { wch: 15 }, // Telefone
        { wch: 30 }, // Atividade
        { wch: 15 }, // Data Criação
        { wch: 15 }, // Data Vencimento
        { wch: 15 }, // Valor Bruto
        { wch: 12 }, // Taxa
        { wch: 15 }, // Valor Líquido
        { wch: 15 }, // Valor Parceiro
        { wch: 15 }, // Valor Estação
        { wch: 20 }, // Forma Pagamento
        { wch: 12 }, // Parcelamento
        { wch: 10 }, // Nota Fiscal
        { wch: 12 }, // É recorrente?
        { wch: 22 }, // Última atualização do pagamento
      ];
      ws['!cols'] = colWidths;

      // Gerar arquivo e download
      const fileName = `Links_Pagamento_${new Date()
        .toLocaleDateString('pt-BR')
        .replace(/\//g, '-')}.xlsx`;
      XLSX.writeFile(wb, fileName);

      dispatch({
        type: 'SNACKBAR',
        payload: {
          message: 'Arquivo exportado com sucesso!',
          variant: 'success',
          open: true,
        },
      });
    } catch (error) {
      console.error('Error exporting payment links:', error);
      dispatch({
        type: 'SNACKBAR',
        payload: {
          message: 'Erro ao exportar dados',
          variant: 'error',
          open: true,
        },
      });
    }
  };
};
